import { Flex, Loading, NoAccessBlock } from "src/components";
import { createContext, useEffect, useMemo, useState } from "react";
import { useDomConfig, useErrorBoundary, useI18n, useAuthorization } from "./hooks";
import { ErrorPage } from "src/pages/Utility/ErrorPage";
import { CustomerSettingsWrapper } from "./wrappers/CustomerSettingsWrapper";
import { initializeSignalR } from "src/realtime-signalr";
import { getAccessToken } from "src/authentication-and-logging/authAndLogger";

type AppState = "loading" | "error" | "no-access" | "not-found" | "idle";

type InfrastructureContextProps = {
	appState: AppState;
	setAppState: (state: AppState) => void;
};

export const InfrastructureContext = createContext<InfrastructureContextProps>({
	appState: "loading",
	setAppState: (_state: AppState) => {},
});

export const InfrastructureWrapper = (props) => {
	// *****************************************
	// #region Properties
	// *****************************************

	const domConfig = useDomConfig();
	const { error } = useErrorBoundary();
	const { language, isLangReady } = useI18n();
	const { isAuthorized, isAuthLoading } = useAuthorization();

	// *****************************************
	// #endregion
	// *****************************************

	// *****************************************
	// #region Lifecycle
	// *****************************************

	const [appState, setAppState] = useState<AppState>("loading");

	useEffect(() => {
		if (isAuthLoading || !isLangReady || (!domConfig && appState !== "loading")) {
			setAppState("loading");
		} else if (!isAuthorized) {
			setAppState("no-access");
		} else if (error) {
			setAppState("error");
		} else {
			setAppState("idle");
		}
	}, [domConfig, error, language, isAuthorized, isAuthLoading, isLangReady]);

	// Initialize SignalR for real-time sensor data
	useEffect(() => {
		if (!domConfig?.API_BASE_URLS?.SIGNALR_MANAGER || !isAuthorized) {
			return;
		}

		const cleanup = initializeSignalR({
			signalRUrl: domConfig.API_BASE_URLS.SIGNALR_MANAGER,
			getAccessToken: getAccessToken,
		});

		return cleanup;
	}, [domConfig, isAuthorized]);

	const context = useMemo(() => {
		return {
			appState,
			setAppState,
		};
	}, [appState, setAppState]);

	// *****************************************
	// #endregion
	// *****************************************

	// *****************************************
	// #region Render
	// *****************************************

	const _renderLoading = () => {
		return (
			<Flex.Column alignment="center" width="100vw" height="100vh">
				<Loading />
			</Flex.Column>
		);
	};

	const _renderError = () => {
		return <ErrorPage />;
	};

	const _renderNoAccess = () => {
		return <NoAccessBlock />;
	};

	const _renderStates = () => {
		switch (appState) {
			case "loading":
				return _renderLoading();
			case "error":
				return _renderError();
			case "no-access":
				return _renderNoAccess();
			default:
				return props.children;
		}
	};

	return (
		<InfrastructureContext.Provider value={context}>
			<CustomerSettingsWrapper>{_renderStates()}</CustomerSettingsWrapper>
		</InfrastructureContext.Provider>
	);

	// *****************************************
	// #endregion
	// *****************************************
};
